import { Field, Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CheckCircleFill, InfoCircle } from 'react-bootstrap-icons';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import { ProcessTypeEnum } from '../../../constants/enums';
import validAllowedCharacters from '../../../constants/valid-character-password';
import { getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IAddEditFormProps } from '../../../models/forms/IForm';
import { IActionFormatter, IReactSelectOption } from '../../../models/ICommon';
import LoaderButton from '../../../shared-components/button/loader-button';
import FormikFormInput from '../../../shared-components/FormikFormHandlers/FormikFormInput';
import FormikFormTextArea from '../../../shared-components/FormikFormHandlers/FormikFormTextArea';
import { ProcessTemplateValidateSchema } from '../../../shared-components/FormikFormHandlers/FormikValidationSchema';
import FormLoader from '../../../shared-components/FormLoader/FormLoader';
import Label from '../../../shared-components/label/label';
import { CloseModalIcon, DeleteIcon, DownIcon, UpIcon } from '../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../translations/format-message';

interface IProps extends IAddEditFormProps<any> {
  allProcessesOptions: IReactSelectOption[];
  allProcess: any[];
}

const getCustomStyles = (selectedProcesses: any[]) => ({
        control: (base) => ({
          ...base,
          // This line disable the blue border
          boxShadow: 'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
          borderRadius: '8px',
          minHeight: '46px',
          border: '1px solid var(--bs-border-color)',
        }),

        option: (base, { isFocused, isSelected, data }) => {
          const isAlreadySelected = selectedProcesses.some(
            (p) => p.ProcessId === Number(data.value)
          );
          return {
            ...base,
            backgroundColor: isFocused
              ? '#33439c'
              : isSelected
              ? '#33439c'
              : isAlreadySelected
              ? 'var(--primary-200)'
              : null, // Light blue on hover, darker blue if selected, primary-200 if already chosen
            color: isFocused ? '#fff' : isSelected ? '#fff' : '#000', // Black text on hover/selected, dark grey otherwise
          };
        },
      });

const AddEditProcessTemplateForm = (props: IProps) => {
  const TEMPLATE_NAME_MAX_LENGTH = 256;
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isDueDatesValid, setIsDueDatesValid] = useState<boolean>(true);
  const [selectedProcessesCount, setSelectedProcessesCount] = useState(0);
  const [selectedProcesses, setSelectedProcesses] = useState<any[]>([]);
  const [selectedProcessesOptions, setSelectedProcessesOptions] = useState<
    any[]
  >([]);
  const processHeaders = [
    // 'Id',
    'process_name',
    'short_name',
    'process_template_due_date_header_text',
    'move',
  ];
  const formikRef = useRef(null);
  const shouldScrollToNewRowRef = useRef(false);
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const [highlightedProcessId, setHighlightedProcessId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (highlightedProcessId === null) return;
    const timer = setTimeout(() => setHighlightedProcessId(null), 1500);
    return () => clearTimeout(timer);
  }, [highlightedProcessId]);

  useEffect(() => {
    if (shouldScrollToNewRowRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      shouldScrollToNewRowRef.current = false;
    }
  }, [selectedProcesses]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        props.initialValues.ProcesTemplates.length
      );
    }
  }, [props.initialValues.ProcesTemplates]);

  useEffect(() => {
    setSelectedProcessesOptions([...props.allProcessesOptions]);
  }, [props.allProcessesOptions]);

  useEffect(() => {
    // Check if props.initialValue and props.initialValue.ProcessTemplate are valid
    if (props.initialValues?.ProcesTemplates) {
      setSelectedProcesses(props.initialValues.ProcesTemplates);
    }
  }, [props.initialValues?.ProcesTemplates]);

  const moveProcessUpHandler = (index: any) => {
    if (index === 0) return;
    const newList = [...selectedProcesses];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setSelectedProcesses([...newList]);
  };

  const moveProcessDownHandler = (index: number) => {
    if (index === selectedProcesses.length - 1) return;
    const newList = [...selectedProcesses];
    [newList[index + 1], newList[index]] = [newList[index], newList[index + 1]];
    setSelectedProcesses([...newList]);
  };

  const handleSelectChange = async (option: any) => {
    const myProcess = props.allProcess.find(
      (p, i) => p.Id === Number(option.value)
    );

    const isAlreadySelected = selectedProcesses.some(
      (p) => p.ProcessId === myProcess.Id
    );
    if (isAlreadySelected) {
      rowRefs.current[myProcess.Id]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setHighlightedProcessId(myProcess.Id);
      return;
    }

    setSelectedProcesses((prev) => {
      const isProcessExist = prev.find((p) => {
        return p.ProcessId === myProcess.Id;
      });

      if (!isProcessExist) {
        shouldScrollToNewRowRef.current = true;
        return [
          ...selectedProcesses,
          {
            Id: 0,
            ProcessId: myProcess.Id,
            ProcessName: myProcess.ProcessName,
            ShortName: myProcess.ShortName,
            DueDates: 0,
            ProcessOrder: selectedProcesses.length + 1,
          },
        ];
      }

      return prev;
    });

    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        selectedProcesses.length + 1
      ); // Set value of numberInput to 10
    }
  };

  const removeProcessHandler = (index: any) => {
    const newList = selectedProcesses.filter((_, i) => i !== index);
    setSelectedProcesses(newList);
    if (formikRef.current) {
      formikRef.current.setFieldValue(
        'SelectedProcesses',
        selectedProcesses.length - 1
      ); // Set value of numberInput to 10
    }
  };

  const getDueDateViolationIndexes = (processes: any[]): number[] => {
    const invalidIndexes: number[] = [];
    for (let index = 1; index < processes.length; index++) {
      const currentDueDate = Number(processes[index]?.DueDates || 0);
      const previousDueDate = Number(processes[index - 1]?.DueDates || 0);
      if (currentDueDate > previousDueDate) {
        invalidIndexes.push(index);
      }
    }
    return invalidIndexes;
  };

  const validateDueDates = (processes: any[]): boolean => {
    return getDueDateViolationIndexes(processes).length === 0;
  };

  useEffect(() => {
    setIsDueDatesValid(validateDueDates(selectedProcesses));
  }, [selectedProcesses]);

  const actionIcons: IActionFormatter[] = [
    {
      dataField: 'up',
      handler: moveProcessUpHandler,
      icon: <UpIcon />,
      isDisable: false,
      title: 'up',
      action: 'up',
      varient: 'primary',
    },
    {
      dataField: 'down',
      handler: moveProcessDownHandler,
      icon: <DownIcon />,
      isDisable: false,
      title: 'down',
      action: 'down',
      varient: 'primary',
    },
    {
      dataField: 'delete',
      handler: removeProcessHandler,
      icon: <DeleteIcon/>,
      isDisable: false,
      title: 'delete',
      action: 'delete',
      varient: 'danger-new',
    },
  ];

  //   useEffect(() => {
  //   if (showModal) {
  //     // Delay to allow modal + Formik to fully render
  //     setTimeout(() => {
  //       bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  //     }, 300); // adjust timing if needed
  //   }
  // }, [showModal, props.initialValues]);

  // Function to handle input changes
  const handleInputChange = (rowIndex, value) => {
    setSelectedProcesses((prevProcesses) =>
      prevProcesses.map((process, index) =>
        index === rowIndex ? { ...process, DueDates: value } : process
      )
    );
    validateDueDates(selectedProcesses);

    // if (selectedProcesses.length > 1) {
    //   for (let i = 0; i < selectedProcesses.length - 1; i++) {
    //     if (selectedProcesses[i].DueDates < value) {
    //       console.error(
    //         `DueDates are not in decreasing order. Error at index ${i + 1}, Id: ${selectedProcesses[i + 1].Id}`
    //       );
    //       return; // Exit once an error is found
    //     }
    //   }
    // }
  };

  return (
    <>
      <Modal
        // className="correction-modal fancy-modal"
        show={true}
        centered
        onHide={() => {}}
        size="xl"
        className="template-modal"
      >
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="add_process_template" />
            ) : (
              <FormattedMessage id="update_process_template" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.isLoading}
              className="Crossicons"
            >
              <CloseModalIcon/>
            </button>
          </Modal.Title>
        </Modal.Header>

        <Formik
          innerRef={formikRef}
          initialValues={{ ...props.initialValues, SelectedProcesses: 0 }}
          enableReinitialize
          validationSchema={ProcessTemplateValidateSchema}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
          validate={(values) => {
            const errors: any = {};
            if (!validateDueDates(selectedProcesses)) {
              errors.selectedProcesses = formatMessage(
                'due_dates_should_be_in_decreasing_order'
              );
            }
            return errors;
          }}
          onSubmit={async (values) => {
            const updatedProcesses = selectedProcesses.map((process, index) => {
              const filter =
                props.allProcess.filter((p) => p.Id === process.ProcessId)[0] ||
                process.Type;
              return {
                ...process,
                ProcessOrder: index + 1,
                Type: ProcessTypeEnum[filter.Type],
                ShortName: process.ShortName,
              };
            });
            props.submitHandler({
              ...values,
              TenantId: Number(getTenantIdFromLocalStorage()),
              ProcesTemplates: updatedProcesses,
            });
          }}
        >
          {({ dirty, errors, values, submitCount }) => {
            const dueDateViolationIndexes =
              getDueDateViolationIndexes(selectedProcesses);
            const hasDueDateOrderError = dueDateViolationIndexes.length > 0;
            const showProcessErrorMessage =
              submitCount > 0 &&
              !!errors.selectedProcesses &&
              hasDueDateOrderError;

            return (
              <Form className="user-form">
                {props.isLoading ? (
                  <FormLoader loading={props.isLoading} contained />
                ) : null}
                <Modal.Body>
                  <div className="row g-4">
                    <div className="col-5">
                      <div className="form-group">
                        <Label
                          className="label-style col-form-label"
                          required={true}
                          label="template_name"
                        />
                        <Field
                          data-testid="testTemplateName"
                          className="form-control"
                          name="TemplateName"
                          type="text"
                          defaultValue={undefined}
                          placeholder=""
                          as={FormikFormInput}
                          maxLength={TEMPLATE_NAME_MAX_LENGTH}
                          validcharacter={validAllowedCharacters}
                          // disabled={props.id > 0}
                        />
                        {String(values.TemplateName || '').length >=
                          TEMPLATE_NAME_MAX_LENGTH && (
                          <div className="invalid-feedback d-block errorMessage">
                            {formatMessage('max_char_limit_256')}
                          </div>
                        )}
                      </div>

                      <div className="form-group mb-0">
                        <Label
                          className="label-style col-form-label"
                          required={false}
                          label="process_template_remarks"
                        />
                        <Field
                          id="Remarks"
                          className="form-control process-textarea"
                          name="Remarks"
                          placeholder=""
                          type="text"
                          as={FormikFormTextArea}
                        />
                      </div>
                    </div>
                    <div className="col-7">
                      <div className="form-group">
                        <label className="label-style col-form-label">
                          <FormattedMessage id="select_process" />
                        </label>

                        <Select  styles={getCustomStyles(selectedProcesses)}
                          className="select-did-process"
                          classNames={{
                            option: (d) =>
                              `our-option ${d.isSelected ? 'active' : ''}`,
                          }}
                          options={selectedProcessesOptions}
                          value={[
                            {
                              label: formatMessage('please_select'),
                              value: '-1',
                            },
                          ]}
                          formatOptionLabel={(option: IReactSelectOption) => {
                            const isOptionSelected = selectedProcesses.some(
                              (p) => p.ProcessId === Number(option.value)
                            );
                            return (
                              <span className="d-flex align-items-center justify-content-between">
                                {option.label}
                                {isOptionSelected && (
                                  <CheckCircleFill
                                    className="ms-2 text-success"
                                    size={14}
                                  />
                                )}
                              </span>
                            );
                          }}
                          onChange={handleSelectChange}
                        />
                        <Field
                          data-testid="testSelectedProcesses"
                          className="form-control d-none"
                          name="SelectedProcesses"
                          type="text"
                          placeholder=""
                          as={FormikFormInput}
                          disabled={props.id > 0}
                        />
                      </div>
                      <div>
                        {errors.selectedProcesses &&
                        !validateDueDates(selectedProcesses) ? (
                          <div className="text-danger">
                            {typeof errors.selectedProcesses === 'string'
                              ? errors.selectedProcesses
                              : String(errors.selectedProcesses)}
                          </div>
                        ) : (
                          <div>{''}</div>
                        )}
                      </div>

                      {
                        <div className="tableDesign row-eq-height tableDevices changingPanel processTable check-item-list border rounded bg-white mt-4">
                          <div className="table-responsive">
                            <div className="react-bootstrap-table">
                              <table className="table">
                                <thead>
                                  <tr>
                                    {processHeaders.map((item, index) => {
                                      return (
                                        <th key={index}>
                                          {formatMessage(item)}
                                          {item ===
                                            'process_template_due_date_header_text' && (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip
                                                    id={`must_be_less_than_or_equal_to_99`}
                                                  >
                                                    {formatMessage(
                                                      'must_be_less_than_or_equal_to_99'
                                                    )}
                                                  </Tooltip>
                                                }
                                              >
                                                <span className="normal-info-svg">
                                                  <InfoCircle size={14} />
                                                </span>
                                              </OverlayTrigger>
                                          )}
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedProcesses &&
                                    selectedProcesses.map(
                                      (process, rowIndex) => {
                                        const isDueDateViolationRow =
                                          showProcessErrorMessage &&
                                          dueDateViolationIndexes.includes(rowIndex);
                                        const highlightStyle = {
                                          backgroundColor:
                                            highlightedProcessId ===
                                            process.ProcessId
                                              ? '#fff1f0'
                                              : undefined,
                                          color:
                                            highlightedProcessId ===
                                            process.ProcessId
                                              ? '#b42318'
                                              : undefined,
                                          transition:
                                            'background-color 0.3s ease',
                                        };
                                        return (
                                        <tr
                                          key={rowIndex}
                                          className={
                                              isDueDateViolationRow
                                                ? 'due-date-order-error-row'
                                                : ''
                                            }
                                          ref={(el) => {
                                            rowRefs.current[
                                              process.ProcessId
                                            ] = el;
                                          }}
                                        >
                                          {/* <td>{process.ProcessId}</td> */}
                                          <td
                                            className="show-Elipses"
                                            title={process.ProcessName}
                                            style={highlightStyle}
                                          >
                                            {process.ProcessName}
                                          </td>
                                          <td
                                            className="show-Elipses"
                                            title={process.ShortName}
                                            style={highlightStyle}
                                          >
                                            {process.ShortName}
                                          </td>
                                          <td style={highlightStyle}>
                                            <input
                                              className={`form-control ${
                                                  isDueDateViolationRow
                                                    ? 'is-invalid due-date-order-error-input'
                                                    : ''
                                                }`}
                                              type="number"
                                              min={0}
                                              max={99}
                                              value={process?.DueDates}
                                              onInvalid={(e) =>
                                                (
                                                  e.target as HTMLInputElement
                                                ).setCustomValidity(
                                                  formatMessage(
                                                    'max_allowed_value_99'
                                                  )
                                                )
                                              }
                                              onInput={(e) =>
                                                (
                                                  e.target as HTMLInputElement
                                                ).setCustomValidity('')
                                              }
                                              onChange={(e) =>
                                                handleInputChange(
                                                  rowIndex,
                                                  parseFloat(e.target.value) ||
                                                    0
                                                )
                                              }
                                            />
                                          </td>
                                          <td style={highlightStyle}>
                                            {
                                              <div className="d-flex">
                                                {actionIcons.map(
                                                  (
                                                    action: IActionFormatter,
                                                    index: number
                                                  ) => {
                                                    return (
                                                      <button
                                                        className={`btn btn-light-${action.varient} btn-sm `}
                                                        key={index}
                                                        title={formatMessage(
                                                          action.title
                                                        )}
                                                        type="button"
                                                        disabled={
                                                          (action.dataField ===
                                                            'up' &&
                                                            rowIndex === 0) ||
                                                          (action.dataField ===
                                                            'down' &&
                                                            rowIndex ===
                                                              selectedProcesses.length -
                                                                1)
                                                        }
                                                        onClick={(event) =>
                                                          action.handler(
                                                            rowIndex
                                                          )
                                                        }
                                                      >
                                                        {action.icon}
                                                      </button>
                                                    );
                                                  }
                                                )}
                                              </div>
                                            }
                                          </td>
                                        </tr>
                                        );
                                      }
                                    )}
                                 <div ref={bottomRef} />
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </Modal.Body>

                <Modal.Footer>
                  <div className="d-flex justify-content-center ">
                    <div className="row tab-action g-0">
                      <div className="col-12">
                        <Button
                          variant="light-danger"
                          type="button"
                          onClick={props.onModalClosed}
                          disabled={props.isLoading}
                        >
                          <FormattedMessage id="cancel" />
                        </Button>
                        <LoaderButton
                          type="submit"
                          isLoading={props.isLoading}
                          text="button_save"
                        />
                      </div>
                    </div>
                  </div>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default AddEditProcessTemplateForm;
